import React, { useEffect, useRef, useState } from "react";
import SectionDivider from "./SectionDivider";
import { generateClient } from "@aws-amplify/api";
import { getSubjectContacts } from "@/graphql/queries";
import moment from "moment";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import * as icons_solid from '@fortawesome/free-solid-svg-icons';
import TranscriptModal from '@/TranscriptModal'; 
import { outcome_color } from '@/utils'; 
import Markdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import remarkBreaks from 'remark-breaks';

const client = generateClient();

const recordingIcon = (<FontAwesomeIcon className="text-primary" icon={icons_solid.faVolumeLow} size="sm" />);
const transcriptIcon = (<FontAwesomeIcon className="text-primary" icon={icons_solid.faQuoteLeft} size="sm" />);

const EngagementSection = ({ subjectId }) => {
  const purple = "#2E2868";
  const grayText = "#4a4a4a";

  const [open, setOpen] = useState(false); 
  const [loading, setLoading] = useState(false);
  const [engagements, setEngagements] = useState([]);
  const [modal, setModal] = useState(); 

  const loadedRef = useRef(false);

  /* Lazy Load Contacts */
  useEffect(() => {
    if (!subjectId || loadedRef.current) return;

    loadedRef.current = true;
    setLoading(true);

    client
      .graphql({
        query: getSubjectContacts,
        variables: {
          subject_id: subjectId,
          sorting: { field: "started_at", order: "desc" },
        },
      })
      .then((res) => {
        const contacts = res?.data?.getSubjectContacts?.subject_contacts || [];

        const mapped = contacts.map((c) => ({
          id: c.contact_id,
          started_at: c.started_at, 
          ended_at: c.ended_at,
          duration_seconds: c.duration_seconds,
          recording_url: c.recording_url,
          transcript_url: c.transcript_url,
          outcomes: c.outcomes || [], 
          summary: c.summary,
          isOpen: false, 
        }));

        setEngagements(mapped);

        if (mapped.length > 0) {
            setOpen(true);
        }
      })
      .finally(() => setLoading(false));
  }, [subjectId]);

  
  const toggleItem = (id) => {
    setEngagements((prev) =>
      prev.map((e) =>
        e.id === id ? { ...e, isOpen: !e.isOpen } : e
      )
    );
  };

  /* Modal Handlers */
  const onCloseModal = () => setModal(null);
  
  const onOpenModal = ({ autoplay = false, event, item } = {}) => {
    event.stopPropagation();
    setModal(
        <TranscriptModal 
            autoplay={autoplay} 
            id={item.id} 
            onClose={onCloseModal} 
            recordingUrl={item.recording_url} 
            transcriptUrl={item.transcript_url} 
        />
    );
  };

  
  const getDuration = duration_seconds => {
      if (!duration_seconds) return null;
      const _d = moment.duration(duration_seconds, 'seconds');
      const h = Math.floor(_d.asHours());
      const m = _d.minutes();
      const s = _d.seconds();
      const d = `${h > 0 ? String(h).padStart(2, '0') + ':' : ''}${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
      return d;
  };

  return (
    <div className="my-3" style={{ maxWidth: "490px" }}>
      {modal} {/* Render Modal Here */}
      
      <div className="p-3">
      {/* Header */}
        <div className="d-flex justify-content-between align-items-center mb-3">
          <div
            className="d-flex align-items-center"
            style={{ cursor: "pointer" }}
            onClick={() => setOpen((o) => !o)}
          >
            <span style={{ fontSize: 30, color: "#b2a9ff", marginRight: 6 }}>
              ⋮⋮
            </span>
            <h5 className="fw-bold m-0" style={{ color: purple }}>
              Engagement
            </h5>
          </div>

          <div
            onClick={() => setOpen((o) => !o)}
            style={{
              width: 28, height: 28, borderRadius: 8,
              backgroundColor: purple, color: "#fff",
              display: "flex", alignItems: "center", justifyContent: "center",
              transition: "transform 0.3s ease",
              transform: open ? "rotate(90deg)" : "rotate(0deg)",
            }}
          >
             <i className="bi bi-chevron-right" style={{ fontSize: 14 }} />
          </div>
        </div>

        {/* Content */}
        {open && (
          <>
            {loading && (
              <div className="text-center my-3">
                <span className="spinner-border spinner-border-sm" />
              </div>
            )}

            {!loading && engagements.length === 0 && (
              <div style={{ fontSize: "13px", opacity: 0.6 }}>
                No engagement history found
              </div>
            )}

            {!loading &&
              engagements.map(eng => {
                const dateLabel = moment(eng.started_at).format("M/DD/YY h:mm A");
                const duration = getDuration(eng.duration_seconds);

                return (
                <div key={eng.id} className="mb-3">
                  {/* Row 1: Date | Duration | Icons */}
                  <div
                    className="d-flex align-items-center flex-wrap mb-1"
                    style={{ gap: "8px", cursor: "pointer" }}
                    onClick={() => toggleItem(eng.id)}
                  >
                    {/* Expand/Collapse Arrow */}
                     <i
                      className={`bi ${eng.isOpen ? "bi-chevron-down" : "bi-chevron-right"}`}
                      style={{ fontSize: "14px", color: purple }}
                    />

                    {/* Date & Time */}
                    <div className="fw-bold" style={{ fontSize: "14px", color: purple }}>
                      {dateLabel}
                    </div>

                    {/* Duration */}
                    {duration && (
                        <div style={{ fontSize: "13px", color: grayText }}>
                            {duration}
                        </div>
                    )}

                    {/* Icons Actions */}
                    <div className="d-flex ms-2">
                        {eng.recording_url && (
                            <div 
                                className="me-2 cursor-pointer" 
                                title="Play Recording"
                                onClick={(e) => onOpenModal({ autoplay: true, event: e, item: eng })}
                            >
                                {recordingIcon}
                            </div>
                        )}
                        <div 
                            className="cursor-pointer" 
                            title="View Transcript"
                            onClick={(e) => onOpenModal({ event: e, item: eng })}
                        >
                            {transcriptIcon}
                        </div>
                    </div>
                  </div>

                  {/* Row 2: Outcomes (Badges) */}
                  {eng.outcomes && eng.outcomes.length > 0 && (
                      <div className="d-flex flex-wrap ms-4 mb-2">
                          {eng.outcomes.map((outcome, i) => (
                              <span 
                                key={i} 
                                className={`badge me-1 mb-1 small ${outcome_color(outcome)}`}
                                style={{ fontSize: '0.75rem', fontWeight: 'normal'}}
                              >
                                  {outcome}
                              </span>
                          ))}
                      </div>
                  )}

                  {/* Row 3: Expanded Summary  */}
                  {eng.isOpen && eng.summary && (
                    <div
                      style={{
                        fontSize: "13px",
                        color: grayText,
                        lineHeight: "1.4",
                        paddingLeft: "24px",
                        marginTop: "4px"
                      }}
                    >
                      <strong style={{ color: purple }}>Summary:</strong> <br/>
                      
                      {/*  Markdown component  */}
                      <Markdown
                          children={eng.summary}
                          remarkPlugins={[remarkGfm, remarkBreaks]}
                          components={{
                              ul: ({ node, ...props }) => (
                                  <ul style={{ margin: 0, paddingLeft: '1.25rem' }} {...props} />
                              ),
                              h1: 'h3',
                              h2: 'h4',
                              h3: 'h4',
                              a: (props) => <a {...props} target="_blank" rel="noopener noreferrer" />,
                              code: ({ inline, children }) =>
                                  !inline ? (
                                  <pre style={{ background: "#f6f8fa", padding: "0.75rem", borderRadius: "6px" }}>
                                      <code>{children}</code>
                                  </pre>
                                  ) : (
                                  <code style={{ background: "#eee", padding: "0.2rem 0.4rem", borderRadius: "4px" }}>{children}</code>
                                  )
                          }}
                      />
                    </div>
                  )}

                
                </div>
              )})}
          </>
        )}
      </div>
    </div>
  );
};

export default EngagementSection;